import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      location: "New York, USA",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      rating: 5,
      tour: "Bali Cultural Experience",
      date: "September 2024",
      content: `TourEase made our Bali trip absolutely unforgettable! The cultural tour was perfectly organized, and our guide was incredibly knowledgeable about local traditions. The temple visits and rice terrace walks were breathtaking. Every detail was taken care of, from transportation to authentic local meals. I can't recommend this platform enough for anyone looking for genuine cultural experiences.`,
      images: [
        "https://images.unsplash.com/photo-1555400082-6c3b6d6d5b6b?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=400&h=300&fit=crop"
      ]
    },
    {
      id: 2,
      name: "Michael Chen",
      location: "Toronto, Canada",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      rating: 5,
      tour: "Santorini Sunset Sailing",
      date: "August 2024",
      content: `The sunset sailing experience in Santorini exceeded all expectations! The crew was professional and friendly, the boat was luxurious, and watching the sunset from the water was magical. The Greek dinner served on board was delicious, and the whole experience felt very exclusive despite being reasonably priced. TourEase's booking process was smooth and hassle-free.`,
      images: [
        "https://images.pexels.com/photos/2901209/pexels-photo-2901209.jpeg?w=400&h=300&fit=crop"
      ]
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      location: "Madrid, Spain",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      rating: 5,
      tour: "Tokyo Food Adventure",
      date: "October 2024",
      content: `As a food lover, the Tokyo street food tour was a dream come true! Our local guide took us to hidden gems that we never would have found on our own. From authentic ramen shops to traditional markets, every stop was a culinary adventure. The night market experience was particularly amazing. TourEase connected us with the perfect local expert who made the city come alive.`,
      images: [
        "https://images.pixabay.com/photo/2020/05/18/16/17/cherry-blossoms-5187041_1280.jpg?w=400&h=300&fit=crop"
      ]
    },
    {
      id: 4,
      name: "David Thompson",
      location: "London, UK",
      avatar: "https://randomuser.me/api/portraits/men/52.jpg",
      rating: 5,
      tour: "Amazon Wildlife Safari",
      date: "July 2024",
      content: `The Amazon rainforest experience was life-changing! Three days of incredible wildlife spotting, river cruises, and learning about indigenous culture. Our guide was passionate about conservation and shared fascinating insights about the ecosystem. The accommodation was comfortable despite being in the middle of the jungle. TourEase delivered an adventure that was both thrilling and educational.`,
      images: [
        "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400&h=300&fit=crop"
      ]
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const currentReview = testimonials?.[currentTestimonial];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What Our Travelers Say
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real experiences from real travelers who have explored the world with TourEase
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-card rounded-3xl shadow-xl p-8 lg:p-12 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary/5 rounded-full translate-y-12 -translate-x-12"></div>

            <div className="relative z-10">
              {/* Quote Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Quote" size={32} className="text-primary" />
                </div>
              </div>

              {/* Rating */}
              <div className="flex justify-center items-center space-x-1 mb-6">
                {[...Array(currentReview?.rating)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-warning fill-current" />
                ))}
              </div>

              {/* Content */}
              <blockquote className="text-lg md:text-xl text-center text-foreground leading-relaxed mb-8 italic">
                "{currentReview?.content}"
              </blockquote>

              {/* Tour Images */}
              {currentReview?.images && currentReview?.images?.length > 0 && (
                <div className="flex justify-center space-x-4 mb-8">
                  {currentReview?.images?.map((image, index) => (
                    <div key={index} className="w-20 h-20 rounded-lg overflow-hidden">
                      <Image
                        src={image}
                        alt={`${currentReview?.tour} memory ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Author Info */}
              <div className="flex items-center justify-center space-x-4">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <Image
                    src={currentReview?.avatar}
                    alt={currentReview?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center">
                  <h4 className="font-semibold text-foreground">{currentReview?.name}</h4>
                  <p className="text-sm text-muted-foreground">{currentReview?.location}</p>
                  <p className="text-sm text-primary font-medium">{currentReview?.tour}</p>
                  <p className="text-xs text-muted-foreground">{currentReview?.date}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full"
            >
              <Icon name="ChevronLeft" size={20} />
            </Button>

            {/* Dots Indicator */}
            <div className="flex space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentTestimonial
                      ? 'bg-primary scale-125' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full"
            >
              <Icon name="ChevronRight" size={20} />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
          {[
            { icon: 'Users', value: '50,000+', label: 'Happy Travelers' },
            { icon: 'Star', value: '4.9/5', label: 'Average Rating' },
            { icon: 'MapPin', value: '500+', label: 'Destinations' },
            { icon: 'Award', value: '98%', label: 'Satisfaction Rate' }
          ]?.map((stat, index) => (
            <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">{stat?.value}</div>
              <div className="text-sm text-muted-foreground">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;